<script setup lang="ts">
import { onMounted, ref } from 'vue'
import TiptapEditor from '@/components/TiptapEditor.vue'
import { newsService } from '@/core/services'
import { useStore } from '@/core/store'
import { useRouter, useRoute } from 'vue-router'
import { flashMessage } from '@/utils/FlashMessages'
import { popupUtil } from '@/utils/Popup'
import { handleError } from '@/utils/ErrorHandler'
import News from '@/core/resources/News'
import TextInput from '@/components/input/TextInput.vue'

const showHTML = ref(false)
const store = useStore()
const article = ref<News>(new News())
const router = useRouter()
const route = useRoute()
const id = ref(Number(route.params.id))

onMounted(() => {
  if (!id.value) return

  newsService
    .get(id.value, store.getAccessToken())
    .then((news) => {
      article.value = news
    })
    .catch(() => {
      router.push({ name: 'new-article' })
    })
})

const createArticle = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to add this article?')
  if (!confirmed) return

  newsService
    .create(article.value, store.getAccessToken())
    .then(() => {
      flashMessage.success('News created successfully')
      router.push({ name: 'news' })
    })
    .catch(handleError)
}

const editArticle = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to edit this article?')
  if (!confirmed) return

  newsService
    .update(article.value, store.getAccessToken())
    .then(() => {
      flashMessage.success('News edited successfully')
      router.push({ name: 'news' })
    })
    .catch(handleError)
}
</script>

<template>
  <div class="container">
    <form novalidate>
      <h3 v-if="!id">Create a new article</h3>
      <h3 v-else>Edit article</h3>

      <div class="form-group">
        <TextInput v-model="article.title" :name="'title'" :id="'title'" :placeholder="'Title'" :type="'text'" :required="true" />

        <h4>Content</h4>

        <span> <input type="checkbox" v-model="showHTML" id="show-html" /> Show HTML </span>

        <TiptapEditor v-model="article.content" v-show="!showHTML" />
        <textarea v-model="article.content" v-show="showHTML"></textarea>
      </div>

      <button v-if="!id" type="button" class="submit-button" @click="createArticle">Create article</button>
      <button v-else type="button" class="submit-button" @click="editArticle">Update article</button>
    </form>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/styles/input.scss';
@import '@/assets/styles/form.scss';

form {
  width: clamp(300px, 70%, 900px);
}

textarea {
  background-color: var(--theme-background);
  height: 236px;
  border: 1px solid var(--theme-border-color);
  border-radius: 8px;
  padding: 8px;
  font-size: 1rem;
  resize: none;
}
</style>
